import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, Fragment as _Fragment, renderList as _renderList } from "vue"

const _hoisted_1 = { class: "label" }
const _hoisted_2 = { class: "calendar__header" }
const _hoisted_3 = { class: "label" }
const _hoisted_4 = {
  key: 0,
  class: "calendar__months"
}
const _hoisted_5 = { class: "calendar__month" }
const _hoisted_6 = { class: "calendar__days-header" }
const _hoisted_7 = { class: "calendar__days-grid" }
const _hoisted_8 = ["onClick", "onMouseenter"]
const _hoisted_9 = {
  key: 0,
  class: "calendar__month"
}
const _hoisted_10 = { class: "calendar__days-header" }
const _hoisted_11 = { class: "calendar__days-grid" }
const _hoisted_12 = ["onMouseenter", "onClick"]
const _hoisted_13 = {
  key: 1,
  class: "calendar__picker calendar__picker--months"
}
const _hoisted_14 = ["onClick"]
const _hoisted_15 = {
  key: 2,
  class: "calendar__picker calendar__picker--years"
}
const _hoisted_16 = ["onClick"]

import {ref, computed, onMounted, onBeforeUnmount, watch} from "vue";
import {Calendar, ChevronLeft, ChevronRight, X} from "utruckin-icons";

// types and props
interface ISelectedDates {
  fromDate: Date;
  toDate: Date;
}

interface IDatePicker {
  mode: "single" | "range";
  isTwoSided?: boolean;
  maxDaysRange?: number;
  defaultRange?: ISelectedDates;
  placement?: "bottom" | "top" | "left" | "right";
  clearable?: boolean;
  selectedDate?: Date
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CommonDatepicker',
  props: {
    mode: { default: "single" },
    isTwoSided: { type: Boolean, default: false },
    maxDaysRange: { default: 0 },
    defaultRange: {},
    placement: { default: "bottom" },
    clearable: { type: Boolean },
    selectedDate: {}
  },
  emits: ["update"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// ref
const currentMonth = ref(new Date());
const startDate = ref<Date | null>(props.defaultRange?.fromDate ?? null);
const endDate = ref<Date | null>(props.defaultRange?.toDate ?? null);
const hoverDate = ref<Date | null>(null);
const pickerEl = ref<HTMLElement | null>(null);
const isOpen = ref(false);
const selectedDate = ref<Date | null>(props.defaultRange?.selectedDate ?? null);
const viewMode = ref("days");
const activePane = ref("left");

// computed

const rightMonth = computed(() => {
  return new Date(
      currentMonth.value.getFullYear(),
      currentMonth.value.getMonth() + 1,
      1,
  );
});

const calendarDays = computed(() => {
  const year = currentMonth.value.getFullYear();
  const month = currentMonth.value.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const days = [];

  for (let i = 0; i < firstDay; i++) {
    days.push({date: null});
  }

  for (let i = 1; i <= daysInMonth; i++) {
    days.push({date: i, fullDate: new Date(year, month, i)});
  }

  return days;
});

const calendarDaysRight = computed(() => {
  const year = rightMonth.value.getFullYear();
  const month = rightMonth.value.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push({date: null});
  for (let i = 1; i <= daysInMonth; i++) {
    days.push({date: i, fullDate: new Date(year, month, i)});
  }
  return days;
});

const yearsStart = ref(new Date().getFullYear() - 8);

const visibleYears = computed(() =>
    Array.from({length: 18}, (_, i) => yearsStart.value + i),
);

const syncYearsStartToCurrent = () => {
  yearsStart.value = currentMonth.value.getFullYear() - 8;
};

// functions
const outsideDatePickerClick = (e: MouseEvent) => {
  if (isOpen.value && !pickerEl.value?.contains(e.target as Node)) {
    if (props.mode === "range" && startDate.value && !endDate.value)
      startDate.value = null;
    hoverDate.value = null;
    isOpen.value = false;
  }
};

const onKeyboardNav = (e: KeyboardEvent) => {
  if (!isOpen.value || viewMode.value !== "days") return;

  if (e.key === "ArrowLeft") {
    e.preventDefault();
    currentMonth.value = new Date(
        currentMonth.value.getFullYear(),
        currentMonth.value.getMonth() - 1,
        1,
    );
  } else if (e.key === "ArrowRight") {
    e.preventDefault();
    currentMonth.value = new Date(
        currentMonth.value.getFullYear(),
        currentMonth.value.getMonth() + 1,
        1,
    );
  }
};

const onClickEsc = (e: KeyboardEvent) => {
  if (e.key === "Escape") {
    if (props.mode === "range" && startDate.value && !endDate.value)
      startDate.value = null;
    hoverDate.value = null;
    isOpen.value = false;
  }
  onKeyboardNav(e);
};

const dayNow = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

const clearCalendar = () => {
  emit('update', startDate.value, endDate.value, selectedDate.value)
}

// hooks
onMounted(() => {
  document.addEventListener("mousedown", outsideDatePickerClick);
  window.addEventListener("keydown", onClickEsc);
});

onBeforeUnmount(() => {
  document.removeEventListener("mousedown", outsideDatePickerClick);
  window.removeEventListener("keydown", onClickEsc);
});

watch(
    () => isOpen.value,
    (newVal) => {
      if (!newVal) {
        emit('update', startDate.value, endDate.value, selectedDate.value)
      }
    }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "c-co-date-picker",
    ref_key: "pickerEl",
    ref: pickerEl
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(["c-co-date-picker__input", isOpen.value ? 'active' : '']),
      onClick: _cache[1] || (_cache[1] = ($event: any) => (
        isOpen.value
          ? (props.mode === 'range' &&
              startDate.value &&
              !endDate.value &&
              (startDate.value = null)
            (isOpen.value = false))
          : ((currentMonth.value = new Date(
              (
                (props.mode === 'single' ? selectedDate.value : startDate.value) ||
                new Date()
              ).getFullYear(),
              (
                (props.mode === 'single' ? selectedDate.value : startDate.value) ||
                new Date()
              ).getMonth(),
              1,
            )),
            (viewMode.value = 'days'),
            syncYearsStartToCurrent(),
            (isOpen.value = true))
      ))
    }, [
      _createElementVNode("div", null, [
        _createVNode(_unref(Calendar), { size: 24 }),
        _createElementVNode("span", _hoisted_1, _toDisplayString(props.mode === "single" && selectedDate.value
                ? selectedDate.value.toLocaleDateString("en-US", {
                  month: "short",
                  day: "2-digit",
                  year: "numeric",
                })
                : props.mode === "range" && startDate.value && endDate.value
                    ? `${startDate.value.toLocaleDateString("en-US", {
                      month: "short",
                      day: "2-digit",
                      year: "numeric",
                    })} — ${endDate.value.toLocaleDateString("en-US", {
                      month: "short",
                      day: "2-digit",
                      year: "numeric",
                    })}`
                    : props.mode === "range"
                        ? "Select range"
                        : "Select date"), 1)
      ]),
      _createElementVNode("div", null, [
        (
            props.clearable &&
            ((props.mode === 'single' && selectedDate.value) ||
              (props.mode === 'range' && (startDate.value || endDate.value)))
          )
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              onClick: _cache[0] || (_cache[0] = _withModifiers(() => {
              props.mode === 'single'
              ? ((selectedDate.value = null), (isOpen.value = false))
              : ((startDate.value = null), (endDate.value = null), (isOpen.value = false));

              clearCalendar()
            }

          , ["stop"]))
            }, [
              _createVNode(_unref(X), { size: 20 })
            ]))
          : _createCommentVNode("", true)
      ])
    ], 2),
    (isOpen.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["calendar", `placement--${props.placement}`])
        }, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("button", {
              onClick: _cache[2] || (_cache[2] = ($event: any) => (
            viewMode.value === 'days'
              ? (currentMonth.value = new Date(
                  currentMonth.value.getFullYear(),
                  currentMonth.value.getMonth() - 1,
                  1,
                ))
              : viewMode.value === 'months'
              ? (currentMonth.value = new Date(
                  currentMonth.value.getFullYear() - 1,
                  currentMonth.value.getMonth(),
                  1,
                ))
              : (yearsStart.value -= 18)
          ))
            }, [
              _createVNode(_unref(ChevronLeft), { size: 20 })
            ]),
            _createElementVNode("span", _hoisted_3, [
              _createElementVNode("button", {
                class: "label-btn",
                onClick: _cache[3] || (_cache[3] = _withModifiers(($event: any) => {
              activePane.value = 'left';
              viewMode.value = 'months';
            }, ["stop"]))
              }, _toDisplayString(currentMonth.value.toLocaleDateString("en-US", {month: "long"})), 1),
              _createElementVNode("button", {
                class: "label-btn",
                onClick: _cache[4] || (_cache[4] = _withModifiers(($event: any) => {
              activePane.value = 'left';
              viewMode.value = 'years';
            }, ["stop"]))
              }, _toDisplayString(currentMonth.value.getFullYear()), 1),
              (props.isTwoSided)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _cache[10] || (_cache[10] = _createTextVNode(" · ", -1)),
                    _createElementVNode("button", {
                      class: "label-btn",
                      onClick: _cache[5] || (_cache[5] = _withModifiers(($event: any) => {
                activePane.value = 'right';
                viewMode.value = 'months';
              }, ["stop"]))
                    }, _toDisplayString(rightMonth.value.toLocaleDateString("en-US", {month: "long"})), 1),
                    _createElementVNode("button", {
                      class: "label-btn",
                      onClick: _cache[6] || (_cache[6] = _withModifiers(($event: any) => {
                activePane.value = 'right';
                viewMode.value = 'years';
              }, ["stop"]))
                    }, _toDisplayString(rightMonth.value.getFullYear()), 1)
                  ], 64))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("button", {
              onClick: _cache[7] || (_cache[7] = ($event: any) => (
            viewMode.value === 'days'
              ? (currentMonth.value = new Date(
                  currentMonth.value.getFullYear(),
                  currentMonth.value.getMonth() + 1,
                  1,
                ))
              : viewMode.value === 'months'
              ? (currentMonth.value = new Date(
                  currentMonth.value.getFullYear() + 1,
                  currentMonth.value.getMonth(),
                  1,
                ))
              : (yearsStart.value += 18)
          ))
            }, [
              _createVNode(_unref(ChevronRight), { size: 20 })
            ])
          ]),
          (viewMode.value === 'days')
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("div", _hoisted_6, [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'], (day) => {
                      return _createElementVNode("div", {
                        key: day,
                        class: "calendar__days-header__item"
                      }, _toDisplayString(day), 1)
                    }), 64))
                  ]),
                  _createElementVNode("div", _hoisted_7, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(calendarDays.value, (day, idx) => {
                      return (_openBlock(), _createElementBlock("div", {
                        onClick: ($event: any) => (
                day.date &&
                  day.fullDate &&
                  dayNow(day.fullDate) <= dayNow(new Date()) &&
                  !(
                    props.mode === 'range' &&
                    props.maxDaysRange &&
                    startDate.value &&
                    !endDate.value &&
                    Math.abs(dayNow(day.fullDate) - dayNow(startDate.value)) >=
                      props.maxDaysRange * 86400000
                  ) &&
                  (props.mode === 'single'
                    ? ((selectedDate.value = day.fullDate), (isOpen.value = false))
                    : !startDate.value || endDate.value
                    ? ((startDate.value = day.fullDate), (endDate.value = null))
                    : ((endDate.value =
                        day.fullDate > startDate.value ? day.fullDate : startDate.value),
                      (startDate.value =
                        day.fullDate < startDate.value ? day.fullDate : startDate.value),
                      (isOpen.value = false)))
              ),
                        onMouseenter: ($event: any) => (hoverDate.value = day.fullDate ?? null),
                        onMouseleave: _cache[8] || (_cache[8] = ($event: any) => (hoverDate.value = null)),
                        key: day.date ? day.fullDate.getTime() : `empty-left-${idx}`,
                        class: _normalizeClass([
                'calendar__days-grid__day',
                {
                  empty: !day.date,
                  disabled:
                    day.fullDate &&
                    (dayNow(day.fullDate) > dayNow(new Date()) ||
                      (props.mode === 'range' &&
                        props.maxDaysRange &&
                        startDate.value &&
                        !endDate.value &&
                        Math.abs(dayNow(day.fullDate) - dayNow(startDate.value)) >=
                          props.maxDaysRange * 86400000)),

                  selected:
                    day.fullDate &&
                    ((props.mode === 'single' &&
                      selectedDate.value &&
                      dayNow(selectedDate.value) === dayNow(day.fullDate)) ||
                      (props.mode === 'range' &&
                        ((startDate.value &&
                          dayNow(startDate.value) === dayNow(day.fullDate)) ||
                          (endDate.value &&
                            dayNow(endDate.value) === dayNow(day.fullDate))))),
                  'in-range':
                    props.mode === 'range' &&
                    startDate.value &&
                    day.fullDate &&
                    ((endDate.value &&
                      dayNow(day.fullDate) > dayNow(startDate.value) &&
                      dayNow(day.fullDate) < dayNow(endDate.value)) ||
                      (!endDate.value &&
                        hoverDate.value &&
                        ((dayNow(hoverDate.value) > dayNow(startDate.value) &&
                          dayNow(day.fullDate) > dayNow(startDate.value) &&
                          dayNow(day.fullDate) <= dayNow(hoverDate.value)) ||
                          (dayNow(hoverDate.value) < dayNow(startDate.value) &&
                            dayNow(day.fullDate) < dayNow(startDate.value) &&
                            dayNow(day.fullDate) >= dayNow(hoverDate.value))))),
                  'range-start':
                    props.mode === 'range' &&
                    startDate.value &&
                    day.fullDate &&
                    dayNow(startDate.value) === dayNow(day.fullDate),

                  'range-end':
                    props.mode === 'range' &&
                    endDate.value &&
                    day.fullDate &&
                    dayNow(endDate.value) === dayNow(day.fullDate),

                  'range-connected-forward':
                    props.mode === 'range' &&
                    startDate.value &&
                    day.fullDate &&
                    dayNow(startDate.value) === dayNow(day.fullDate) &&
                    ((endDate.value && dayNow(endDate.value) > dayNow(startDate.value)) ||
                      (!endDate.value &&
                        hoverDate.value &&
                        dayNow(hoverDate.value) > dayNow(startDate.value))),

                  'range-connected-backward':
                    props.mode === 'range' &&
                    startDate.value &&
                    day.fullDate &&
                    dayNow(startDate.value) === dayNow(day.fullDate) &&
                    !endDate.value &&
                    hoverDate.value &&
                    dayNow(hoverDate.value) < dayNow(startDate.value),
                },
              ])
                      }, _toDisplayString(day.date), 43, _hoisted_8))
                    }), 128))
                  ])
                ]),
                (props.isTwoSided)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                      _createElementVNode("div", _hoisted_10, [
                        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'], (day) => {
                          return _createElementVNode("div", {
                            key: day,
                            class: "calendar__days-header__item"
                          }, _toDisplayString(day), 1)
                        }), 64))
                      ]),
                      _createElementVNode("div", _hoisted_11, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(calendarDaysRight.value, (day, idx) => {
                          return (_openBlock(), _createElementBlock("div", {
                            onMouseenter: ($event: any) => (hoverDate.value = day.fullDate ?? null),
                            onMouseleave: _cache[9] || (_cache[9] = ($event: any) => (hoverDate.value = null)),
                            key: day.date ? day.fullDate.getTime() : `empty-right-${idx}`,
                            onClick: ($event: any) => (
                day.date &&
                  day.fullDate &&
                  dayNow(day.fullDate) <= dayNow(new Date()) &&
                  !(
                    props.mode === 'range' &&
                    props.maxDaysRange &&
                    startDate.value &&
                    !endDate.value &&
                    Math.abs(dayNow(day.fullDate) - dayNow(startDate.value)) >=
                      props.maxDaysRange * 86400000
                  ) &&
                  (props.mode === 'single'
                    ? ((selectedDate.value = day.fullDate), (isOpen.value = false))
                    : !startDate.value || endDate.value
                    ? ((startDate.value = day.fullDate), (endDate.value = null))
                    : ((endDate.value =
                        day.fullDate > startDate.value ? day.fullDate : startDate.value),
                      (startDate.value =
                        day.fullDate < startDate.value ? day.fullDate : startDate.value),
                      (isOpen.value = false)))
              ),
                            class: _normalizeClass([
                'calendar__days-grid__day',
                {
                  empty: !day.date,
                  disabled:
                    day.fullDate &&
                    (dayNow(day.fullDate) > dayNow(new Date()) ||
                      (props.mode === 'range' &&
                        props.maxDaysRange &&
                        startDate.value &&
                        !endDate.value &&
                        Math.abs(dayNow(day.fullDate) - dayNow(startDate.value)) >=
                          props.maxDaysRange * 86400000)),

                  selected:
                    day.fullDate &&
                    ((props.mode === 'single' &&
                      selectedDate.value &&
                      dayNow(selectedDate.value) === dayNow(day.fullDate)) ||
                      (props.mode === 'range' &&
                        ((startDate.value &&
                          dayNow(startDate.value) === dayNow(day.fullDate)) ||
                          (endDate.value &&
                            dayNow(endDate.value) === dayNow(day.fullDate))))),
                  'in-range':
                    props.mode === 'range' &&
                    startDate.value &&
                    day.fullDate &&
                    ((endDate.value &&
                      dayNow(day.fullDate) > dayNow(startDate.value) &&
                      dayNow(day.fullDate) < dayNow(endDate.value)) ||
                      (!endDate.value &&
                        hoverDate.value &&
                        ((dayNow(hoverDate.value) > dayNow(startDate.value) &&
                          dayNow(day.fullDate) > dayNow(startDate.value) &&
                          dayNow(day.fullDate) <= dayNow(hoverDate.value)) ||
                          (dayNow(hoverDate.value) < dayNow(startDate.value) &&
                            dayNow(day.fullDate) < dayNow(startDate.value) &&
                            dayNow(day.fullDate) >= dayNow(hoverDate.value))))),
                  'range-start':
                    props.mode === 'range' &&
                    startDate.value &&
                    day.fullDate &&
                    dayNow(startDate.value) === dayNow(day.fullDate),

                  'range-end':
                    props.mode === 'range' &&
                    endDate.value &&
                    day.fullDate &&
                    dayNow(endDate.value) === dayNow(day.fullDate),

                  'range-connected-forward':
                    props.mode === 'range' &&
                    startDate.value &&
                    day.fullDate &&
                    dayNow(startDate.value) === dayNow(day.fullDate) &&
                    ((endDate.value && dayNow(endDate.value) > dayNow(startDate.value)) ||
                      (!endDate.value &&
                        hoverDate.value &&
                        dayNow(hoverDate.value) > dayNow(startDate.value))),

                  'range-connected-backward':
                    props.mode === 'range' &&
                    startDate.value &&
                    day.fullDate &&
                    dayNow(startDate.value) === dayNow(day.fullDate) &&
                    !endDate.value &&
                    hoverDate.value &&
                    dayNow(hoverDate.value) < dayNow(startDate.value),
                },
              ])
                          }, _toDisplayString(day.date), 43, _hoisted_12))
                        }), 128))
                      ])
                    ]))
                  : _createCommentVNode("", true)
              ]))
            : (viewMode.value === 'months')
              ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                  (_openBlock(), _createElementBlock(_Fragment, null, _renderList([
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
          ], (m, i) => {
                    return _createElementVNode("button", {
                      key: m,
                      class: _normalizeClass(["calendar__pick-item", {
            active:
              activePane.value === 'left'
                ? i === currentMonth.value.getMonth()
                : i === rightMonth.value.getMonth(),
          }]),
                      onClick: ($event: any) => (
            activePane.value === 'left'
              ? (currentMonth.value = new Date(currentMonth.value.getFullYear(), i, 1))
              : (currentMonth.value = new Date(currentMonth.value.getFullYear(), i, 1)),
              (viewMode.value = 'days')
          )
                    }, _toDisplayString(m), 11, _hoisted_14)
                  }), 64))
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_15, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleYears.value, (y) => {
                    return (_openBlock(), _createElementBlock("button", {
                      key: y,
                      class: _normalizeClass(["calendar__pick-item", {
            active:
              activePane.value === 'left'
                ? y === currentMonth.value.getFullYear()
                : y === rightMonth.value.getFullYear(),
          }]),
                      onClick: ($event: any) => (
            activePane.value === 'left'
              ? (currentMonth.value = new Date(y, currentMonth.value.getMonth(), 1))
              : (currentMonth.value = new Date(y, rightMonth.value.getMonth() + 1, 1)),
              (viewMode.value = 'months')
          )
                    }, _toDisplayString(y), 11, _hoisted_16))
                  }), 128))
                ]))
        ], 2))
      : _createCommentVNode("", true)
  ], 512))
}
}

})